import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, Globe, Server, Users, Clock, AlertTriangle, FileText, Mail, Phone, ChevronRight } from 'lucide-react';

const PrivacyPolicy = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero section */}
      <div className="relative bg-gradient-to-b from-indigo-900 to-purple-900 py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              How we collect, use, protect, and handle your information across our platform
            </p>
            <div className="flex justify-center mt-6">
              <div className="inline-flex items-center gap-2 text-sm text-indigo-300 bg-indigo-500/10 rounded-full px-4 py-1 border border-indigo-500/20">
                <Clock className="w-4 h-4" />
                Last Updated: {new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md">
          <div className="p-6 md:p-8 lg:p-10">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="prose prose-lg max-w-none text-gray-700"
            >
              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                  <Shield className="w-6 h-6 text-indigo-600" />
                  1. Introduction
                </h2>
                <p>
                  LoadLink ("we", "our", or "us") is committed to protecting your privacy and safeguarding your personal data. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our logistics platform services, website, and mobile applications (collectively, the "Services").
                </p>
                <p>
                  By accessing or using our Services, you consent to the practices described in this Privacy Policy. If you do not agree with this Privacy Policy, please do not use our Services.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Database className="w-6 h-6 text-indigo-600" />
                  2. Information We Collect
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Personal Information</h3>
                <p>We collect the following types of personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, phone number, password, profile picture, and biographical information</li>
                  <li><strong>Business Information:</strong> Company name, MC number, DOT number, and company address</li>
                  <li><strong>Location Information:</strong> Physical and billing addresses, GPS location data from mobile applications</li>
                  <li><strong>Payment Information:</strong> Payment authorizations and billing details</li>
                  <li><strong>Identity Verification:</strong> Documentation required for carrier and customer verification</li>
                  <li><strong>Device Information:</strong> IP address, browser type, device type, operating system, and mobile device identifiers</li>
                  <li><strong>Usage Data:</strong> Login timestamps, session duration, feature usage, and interaction with the platform</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Vehicle and Telematics Data</h3>
                <p>If you are a carrier or driver using our Services, we collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Vehicle location data</li>
                  <li>Speed and fuel level information</li>
                  <li>Engine status</li>
                  <li>Route information</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Communications</h3>
                <p>We collect information from your communications with us, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Messages through our in-platform messaging system</li>
                  <li>Communications related to loads, bids, and disputes</li>
                  <li>Customer service interactions</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Eye className="w-6 h-6 text-indigo-600" />
                  3. How We Collect Information
                </h2>
                <p>We collect information through:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Direct Submission:</strong> Information you provide when registering, updating your profile, or using our Services</li>
                  <li><strong>Automated Technologies:</strong> Cookies, web beacons, and similar technologies</li>
                  <li><strong>Mobile Applications:</strong> GPS and device sensors when location services are enabled</li>
                  <li><strong>Third-Party Sources:</strong> Public databases, identity verification services, and business partners</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Server className="w-6 h-6 text-indigo-600" />
                  4. How We Use Your Information
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Core Service Functions</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account creation and management</li>
                  <li>Processing and managing load postings and bids</li>
                  <li>Facilitating driver assignments and load tracking</li>
                  <li>Processing payments and billing operations</li>
                  <li>Document generation and management</li>
                  <li>Identity verification and security</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Communication</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Sending operational notifications about loads, bids, and transactions</li>
                  <li>Providing real-time updates on shipment status</li>
                  <li>Facilitating communication between customers, carriers, and drivers</li>
                  <li>Sending service updates and announcements</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Platform Improvement</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Analyzing usage patterns to enhance the platform</li>
                  <li>Monitoring system performance and reliability</li>
                  <li>Developing new features and services</li>
                  <li>Troubleshooting technical issues</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.4 Security and Compliance</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Protecting your account and data</li>
                  <li>Detecting and preventing fraud</li>
                  <li>Complying with legal obligations</li>
                  <li>Enforcing our terms of service</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Users className="w-6 h-6 text-indigo-600" />
                  5. How We Share Your Information
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Platform Participants</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Load Information:</strong> Shared between participating customers, carriers, and assigned drivers</li>
                  <li><strong>Location Data:</strong> Shared with relevant parties involved in active shipments</li>
                  <li><strong>Document Access:</strong> Provided to authorized parties based on role-specific permissions</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Service Providers</h3>
                <p>We share information with trusted service providers who help us operate our platform:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Cloud infrastructure providers for hosting</li>
                  <li>Payment processors for financial transactions</li>
                  <li>Communication services for notifications</li>
                  <li>Analytics providers for platform optimization</li>
                  <li>Document management services</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.3 Legal Requirements</h3>
                <p>We may disclose information when required by law:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>In response to valid legal requests from public authorities</li>
                  <li>To protect our rights, privacy, safety, or property</li>
                  <li>To detect, prevent, or address fraud and security issues</li>
                  <li>In connection with legal proceedings or investigations</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Lock className="w-6 h-6 text-indigo-600" />
                  6. Data Security
                </h2>
                <p>We implement appropriate technical and organizational measures to protect your personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>End-to-end encryption for sensitive data transmission</li>
                  <li>Secure document storage with strict access controls</li>
                  <li>Role-based access management system</li>
                  <li>Regular security audits and vulnerability testing</li>
                  <li>Comprehensive security headers for web and mobile applications</li>
                  <li>Encrypted storage for sensitive credentials and personal information</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Clock className="w-6 h-6 text-indigo-600" />
                  7. Data Retention
                </h2>
                <p>We retain your personal information for as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide our Services</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes</li>
                  <li>Enforce our agreements</li>
                </ul>
                <p className="mt-4">After the retention period expires, we securely delete or anonymize your data.</p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <FileText className="w-6 h-6 text-indigo-600" />
                  8. Your Privacy Rights
                </h2>
                <p>Depending on your location, you may have rights regarding your personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request copies of your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Restriction:</strong> Request limitation of how we use your data</li>
                  <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                  <li><strong>Objection:</strong> Object to certain uses of your data</li>
                </ul>
                <p className="mt-4">To exercise these rights, please contact us using the information in Section 12.</p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Globe className="w-6 h-6 text-indigo-600" />
                  9. International Data Transfers
                </h2>
                <p>
                  Our Services operate globally, which may involve transferring and storing your data in countries outside your residence. We ensure appropriate safeguards are in place to protect your information during these transfers.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <AlertTriangle className="w-6 h-6 text-indigo-600" />
                  10. Children's Privacy
                </h2>
                <p>
                  Our Services are not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we discover we have collected information from a child, we will delete it promptly.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Clock className="w-6 h-6 text-indigo-600" />
                  11. Changes to This Privacy Policy
                </h2>
                <p>
                  We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes through our Services or by email. Your continued use of our Services after such modifications constitutes acceptance of the updated Privacy Policy.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Mail className="w-6 h-6 text-indigo-600" />
                  12. Contact Us
                </h2>
                <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:</p>
                <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p><strong>Email:</strong> <a href="mailto:joe@loadlinkco.com" className="text-indigo-600 hover:text-indigo-800">joe@loadlinkco.com</a></p>
                  <p className="mt-2"><strong>Address:</strong><br />
                    LoadLink Company<br />
                    13950 Frazho Rd<br />
                    Warren, MI 48089
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Related links section */}
        <div className="max-w-4xl mx-auto mt-10">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Related Documents</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link 
              to="/terms-of-service"
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-indigo-600 mr-3" />
                <span>Terms of Service</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            <Link 
              to="/security"
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-indigo-600 mr-3" />
                <span>Security Policy</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          </div>
        </div>

        {/* Contact section */}
        <div className="max-w-4xl mx-auto mt-10 bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <div className="flex flex-col sm:flex-row items-center">
            <div className="mb-6 sm:mb-0 sm:mr-6">
              <div className="bg-indigo-100 p-4 rounded-full">
                <Phone className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Still have questions about your data?</h3>
              <p className="text-gray-600 mb-4">Our privacy team is here to help you understand how we handle your information.</p>
              <a 
                href="mailto:privacy@loadlink.com" 
                className="inline-flex items-center bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Contact Privacy Team
                <ChevronRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 